<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';

import { useAuthStore } from '@/features/auth/authStore';

const router = useRouter();
const auth = useAuthStore();
const query = ref('');

function onSearch(): void {
  const q = query.value.trim();
  router.push({ path: '/units', query: q ? { q } : {} });
}

async function onSignOut(): Promise<void> {
  await auth.logout();
  await router.push('/');
}
</script>

<template>
  <header class="app-header" data-testid="app-header">
    <div class="nav-wrapper">
      <nav>
        <RouterLink class="logo" data-testid="logo" to="/">
          <span class="logo-mark" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="1" y="11" width="20" height="20" rx="2" fill="#065c0e" stroke="#000" stroke-width="1.5" />
              <rect x="11" y="1" width="20" height="20" rx="2" fill="#065c0e" stroke="#000" stroke-width="1.5" />
            </svg>
          </span>
          <span class="logo-text">Nori Skillsets</span>
        </RouterLink>

        <RouterLink class="nav-link" to="/units">Browse</RouterLink>
        <RouterLink class="nav-link" to="/new">New</RouterLink>

        <span class="spacer"></span>

        <form
          class="search"
          role="search"
          data-testid="search-form"
          @submit.prevent="onSearch"
        >
          <input
            v-model="query"
            type="search"
            class="search-input"
            data-testid="search-input"
            placeholder="Search the registry…"
            aria-label="Search the registry"
          />
          <button type="submit" class="btn btn-primary">Search</button>
        </form>

        <div class="auth-cluster" data-testid="auth-cluster">
          <template v-if="auth.isAuthenticated">
            <span class="user-name font-medium">{{ auth.displayName }}</span>
            <RouterLink class="nav-link" to="/profile">Profile</RouterLink>
            <!-- Settings is the admin home (it now hosts Manage users); it is
                 shown only to admins per APPLICATION_SPEC item 2. -->
            <RouterLink v-if="auth.isAdmin" class="nav-link" to="/settings">Settings</RouterLink>
            <button
              type="button"
              class="nav-link signout-btn"
              data-testid="header-signout"
              @click="onSignOut"
            >
              Sign out
            </button>
          </template>
          <template v-else>
            <RouterLink class="nav-link" to="/signin">Sign in</RouterLink>
          </template>
        </div>
      </nav>
    </div>
  </header>
</template>

<style scoped>
.app-header {
  border-bottom: 1px solid var(--border);
  background: var(--background);
}

.nav-wrapper {
  max-width: 1200px;
  margin: 0 auto;
}

nav {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 0.75rem 2rem;
}

.logo {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-display);
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--foreground);
}

.logo-mark {
  display: inline-flex;
}

.nav-link {
  color: var(--muted-foreground);
  font-weight: 500;
  font-size: 0.875rem;
}

.nav-link:hover {
  color: var(--foreground);
}

.signout-btn {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
}

.spacer {
  flex: 1;
}

.search {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.search-input {
  padding: 0.4rem 0.6rem;
  font-size: 0.875rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--background);
  color: var(--foreground);
}

.search-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 2px rgba(24, 24, 27, 0.1);
}

.auth-cluster {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.user-name {
  font-size: 0.875rem;
}
</style>
